import {
	ApplicationConfig,
	ErrorHandler,
	importProvidersFrom,
	inject,
	provideAppInitializer,
} from '@angular/core';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import {
	AGILOX_API_ENDPOINT,
	App,
	APP_NAME,
	APP_SETTINGS_ADAPTER,
	AppNavMenuPointsService,
	CachedRouteReuseStrategy,
	ChunkLoadingFailedErrorHandler,
	ENVIRONMENT,
	FleetEventsService,
	headersInterceptor,
	initializeTranslations,
	localeProvider,
	MENU_POINTS,
	MenuPointService,
	PLAUSIBLE_DOMAIN,
	PlausibleService,
	provideDatepickerTranslationsWithNgxTranslate,
	provideFavicon,
	provideSelectTranslationsWithNgxTranslate,
	SummaryOfTheYearService,
	ThemeService,
	TranslationsLoader,
	USER_FORMAT,
} from '@agilox/common';
import {
	HttpClient,
	provideHttpClient,
	withInterceptors,
	withInterceptorsFromDi,
	withXhr,
} from '@angular/common/http';
import { WidgetsModule } from './general/widgets/widgets.module';
import { cacheOptimizerInterceptor } from './general/interceptors/cache-optimizer.interceptor';
import { responseInterceptor } from './general/interceptors/response.interceptor';
import { createErrorHandlerInterceptor } from '@agilox/interceptors';
import { provideState, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { AppSettingsEffects } from './store/app-settings.effects';
import { VehicleFilterEffects } from './store/vehicles-filter/vehicle-filter.effects';
import { DateFilterEffects } from './store/date-filter/date-filter.effects';
import { ModeFilterEffect } from './store/mode-filter/mode-filter.effect';
import { WorkflowShiftEffect } from './store/workflow-shift/workflow-shift.effect';
import { DateFilterAndWorkflowShiftEffects } from './store/date-filter-and-workflow-shift/date-filter-and-workflow-shift.effect';
import { VehiclesStateEffects } from './store/vehicles/vehicles.effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideRouter, RouteReuseStrategy, Router, withHashLocation } from '@angular/router';
import { provideUserContext, UserStoreService } from '@user-store';
import { DatePipe, DecimalPipe } from '@angular/common';
import { initializeAnalyticsApp } from './general/utils/analytics-initialization.util';
import { vehiclesModelFilterInterceptor } from './general/interceptors/vehicles-model-filter.interceptor';
import * as Sentry from '@sentry/angular';
import { ToastService, VehiclesSelectService } from '@agilox/components';
import { CustomDatePipe } from '@agilox/pipe/custom-date';
import { CustomNumberPipe, provideNumberFormatter } from '@agilox/pipe/custom-number';
import { CustomTimePipe } from '@agilox/pipe/custom-time';
import { NavListModule, NavModule, InputModule } from '@agilox/ui';
import { environment } from '@analytics/env/environment';
import { provideAnimations } from '@angular/platform-browser/animations';
import { AuthModule, AuthService, AUTH_CONFIG, MSALAUTHConfigFactory } from '@auth';
import { appRoutes } from './app.routes';
import { MaterialModule } from './general/material/material.module';
import { AnalyticsAppSettingsAdapter } from './general/shared/classes/app-settings.adapter';
import { AnalyticsMenuPointService } from './general/shared/services/analytics-menupoint/analytics-menupoint.service';
import { desktopMenuPoints } from './general/shared/services/authentication/desktop-menupoints';
import { mobileMenuPoints } from './general/shared/services/authentication/mobile-menupoints';
import { VehiclesService } from './general/shared/services/vehicles/vehicles.service';
import { dateFilterFeature } from './store/date-filter/date-filter.state';
import { modeFilterFeature } from './store/mode-filter/mode-filter.state';
import { vehicleListViewFeature } from './store/vehicle-list-view/vehicle-list-view.state';
import { vehicleFilterFeature } from './store/vehicles-filter/vehicles-filter.state';
import { vehiclesStateFeature } from './store/vehicles/vehicles.state';
import { workflowShiftFeature } from './store/workflow-shift/workflow-shift.state';

export const appConfig: ApplicationConfig = {
	providers: [
		importProvidersFrom(
			MaterialModule,
			TranslateModule.forRoot({
				loader: {
					provide: TranslateLoader,
					useClass: TranslationsLoader,
					deps: [HttpClient],
				},
			}),
			WidgetsModule,
			NavListModule,
			NavModule,
			InputModule,
			AuthModule
		),
		provideRouter(appRoutes, withHashLocation()),
		{ provide: RouteReuseStrategy, useClass: CachedRouteReuseStrategy },
		provideHttpClient(
			withXhr(),
			withInterceptors([
				vehiclesModelFilterInterceptor,
				cacheOptimizerInterceptor,
				createErrorHandlerInterceptor({ app: App.analytics, environment, onError: 'swallow' }),
				headersInterceptor,
				responseInterceptor,
			]),
			withInterceptorsFromDi() // needed for MSAL
		),
		provideDatepickerTranslationsWithNgxTranslate(),
		provideSelectTranslationsWithNgxTranslate(),
		provideStore(),
		provideState(dateFilterFeature),
		provideState(vehicleFilterFeature),
		provideState(modeFilterFeature),
		provideState(workflowShiftFeature),
		provideState(vehicleListViewFeature),
		provideState(vehiclesStateFeature),
		provideEffects([
			AppSettingsEffects,
			VehicleFilterEffects,
			DateFilterEffects,
			ModeFilterEffect,
			WorkflowShiftEffect,
			DateFilterAndWorkflowShiftEffects,
			VehiclesStateEffects,
		]),
		provideStoreDevtools(),
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler({
				showDialog: false,
			}),
		},
		TranslateService,
		provideAppInitializer(() => {
			const initializerFn = initializeTranslations(inject(TranslateService));
			return initializerFn();
		}),
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		// insert errorhandlers after sentry, otherwise sentry interrupts too early in error-handling
		{
			provide: ErrorHandler,
			useClass: ChunkLoadingFailedErrorHandler,
		},
		UserStoreService,
		...provideUserContext(),
		DatePipe,
		provideAppInitializer(() => {
			const authService = inject(AuthService);
			const userStore = inject(UserStoreService);
			const vehicleService = inject(VehiclesService);
			return initializeAnalyticsApp(authService, userStore, vehicleService);
		}),
		provideAppInitializer(() => {
			inject(ThemeService);
		}),
		provideFavicon(),
		AppNavMenuPointsService,
		{
			provide: MENU_POINTS,
			useValue: {
				desktop: desktopMenuPoints,
				mobile: mobileMenuPoints,
			},
		},
		{ provide: MenuPointService, useClass: AnalyticsMenuPointService },
		{
			provide: USER_FORMAT,
			useFactory: (userStore: UserStoreService) => userStore.userFormat,
			deps: [UserStoreService],
		},
		{
			provide: AUTH_CONFIG,
			useFactory: MSALAUTHConfigFactory,
			deps: [ENVIRONMENT],
		},
		{
			provide: ENVIRONMENT,
			useValue: environment,
		},
		{
			provide: APP_NAME,
			useValue: App.analytics,
		},
		{
			provide: APP_SETTINGS_ADAPTER,
			useClass: AnalyticsAppSettingsAdapter,
		},
		{
			provide: PLAUSIBLE_DOMAIN,
			useValue: 'analytics.agilox.net',
		},
		PlausibleService,
		provideAppInitializer(() => {
			inject(PlausibleService);
		}),
		{
			provide: AGILOX_API_ENDPOINT,
			useValue: environment.server,
		},
		localeProvider,
		DecimalPipe,
		CustomDatePipe,
		CustomTimePipe,
		CustomNumberPipe,
		...provideNumberFormatter(),
		ToastService,
		VehiclesSelectService,
		FleetEventsService,
		SummaryOfTheYearService,
		provideAnimations(),
	],
};
